import {
	TQrColorScheme,
	welcomeImageAtom,
	welcomeImageFileAtom,
} from 'features/generator/contentFormSections'
import { colorSchemeOptionListAtom } from 'features/generator/contentFormSections/client/ContentDesignForm'
import {
	documentCompanyNameAtom,
	documentFileDescriptionAtom,
	documentTitleAtom,
	documentWebsiteAtom,
} from 'features/generator/contentFormSections/PdfForm/DocumentInfo'
import {
	pdfAtom,
	pdfFileAtom,
	pdfIsDirectAtom,
} from 'features/generator/contentFormSections/PdfForm/PdfFile'
import { atom } from 'jotai'
import { TUploadedMedia } from 'services/mediaStorage'

import {
	primaryColorAtom,
	secondaryColorAtom,
	selectedColorSchemeAtom,
} from '@/content-from-sections/client'
import { getFileFromMedia, isOnServer } from '@/utils'

import { QR_TYPE } from '../../generatorTypes'

export type TPdfContent = {
	type: QR_TYPE.Pdf
	colors: Omit<TQrColorScheme, 'name'>
	file: TUploadedMedia | null
	company: {
		name: string
		title: string
		website: string
		description: string
	}
	isDirect: boolean
	loaderImage: TUploadedMedia
}

export const emptyImage = {
	publicId: '',
	resourceType: '',
	format: '',
	bytes: 0,
} as TUploadedMedia

export const pdfContentAtom = atom<TPdfContent, [TPdfContent], void>(
	(get) => ({
		type: QR_TYPE.Pdf,
		colors: {
			primary: get(primaryColorAtom),
			secondary: get(secondaryColorAtom),
		},
		file: get(pdfAtom),
		company: {
			name: get(documentCompanyNameAtom),
			title: get(documentTitleAtom),
			website: get(documentWebsiteAtom),
			description: get(documentFileDescriptionAtom),
		},
		isDirect: get(pdfIsDirectAtom),
		loaderImage: get(welcomeImageAtom) || emptyImage,
	}),
	(get, set, content: TPdfContent) => {
		// Color scheme
		const colorScheme = get(colorSchemeOptionListAtom).find(
			(scheme) =>
				scheme.primary === content.colors.primary &&
				scheme.secondary === content.colors.secondary,
		)

		set(selectedColorSchemeAtom, colorScheme?.name ?? '')
		set(primaryColorAtom, content.colors.primary)
		set(secondaryColorAtom, content.colors.secondary)

		set(pdfAtom, content.file)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					if (content.file) {
						const pdfFile = await getFileFromMedia(content.file)
						set(pdfFileAtom, pdfFile)
					}
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
		set(pdfIsDirectAtom, content.isDirect)
		set(documentCompanyNameAtom, content.company.name)
		set(documentTitleAtom, content.company.title)
		set(documentWebsiteAtom, content.company.website)
		set(documentFileDescriptionAtom, content.company.description)
		set(welcomeImageAtom, content.loaderImage)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const loaderFile = await getFileFromMedia(content.loaderImage)
					set(welcomeImageFileAtom, loaderFile)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
	},
)
